import React, { useState } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, IconButton, Chip, Box, Typography, TextField, MenuItem, Select, FormControl, Button, Tooltip } from '@mui/material';
import { Visibility as VisibilityIcon, FilterAltOff as FilterAltOffIcon } from '@mui/icons-material';
interface Column {
  id: string;
  label: string;
  filterable?: boolean;
  filterType?: 'text' | 'select' | 'date' | 'number';
  filterOptions?: string[];
  format?: (value: any, row?: any) => string | React.ReactNode;
}
interface UnifiedTransactionTableProps {
  columns: Column[];
  data: any[];
  onViewDetails: (row: any) => void;
  title?: string;
  emptyMessage?: string;
}
const UnifiedTransactionTable: React.FC<UnifiedTransactionTableProps> = ({
  columns,
  data,
  onViewDetails,
  title = 'Transaction List',
  emptyMessage = 'No transactions found'
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState<{
    [key: string]: any;
  }>({});
  const hasActiveFilters = Object.keys(filters).length > 0;
  const handleFilterChange = (columnId: string, value: any) => {
    setFilters({
      ...filters,
      [columnId]: value
    });
    setPage(0);
  };
  const handleClearAllFilters = () => {
    setFilters({});
    setPage(0);
  };
  const handleClearFilter = (columnId: string) => {
    const newFilters = {
      ...filters
    };
    delete newFilters[columnId];
    setFilters(newFilters);
    setPage(0);
  };
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Pending':
      case 'Pending Checker Approval':
        return 'warning';
      case 'Processing':
      case 'In-Process':
        return 'info';
      default:
        return 'default';
    }
  };
  // Apply filters
  const filteredData = data.filter(row => {
    return Object.keys(filters).every(columnId => {
      const filterValue = filters[columnId];
      if (!filterValue || filterValue === 'all') return true;
      const column = columns.find(col => col.id === columnId);
      if (!column) return true;
      const cellValue = row[columnId];
      if (column.filterType === 'text') {
        return String(cellValue).toLowerCase().includes(String(filterValue).toLowerCase());
      } else if (column.filterType === 'select') {
        return cellValue === filterValue;
      } else if (column.filterType === 'date') {
        if (filterValue.from && new Date(cellValue) < new Date(filterValue.from)) return false;
        if (filterValue.to && new Date(cellValue) > new Date(filterValue.to)) return false;
        return true;
      } else if (column.filterType === 'number') {
        if (filterValue.min && Number(cellValue) < Number(filterValue.min)) return false;
        if (filterValue.max && Number(cellValue) > Number(filterValue.max)) return false;
        return true;
      }
      return true;
    });
  });
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  return <Paper elevation={1} data-id="element-2650">
      <Box sx={{
      p: 2,
      borderBottom: 1,
      borderColor: 'divider',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }} data-id="element-2651">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }} data-id="element-2652">
          <Typography variant="h6" data-id="element-2653">{title}</Typography>
          {hasActiveFilters && <Button size="small" variant="text" color="primary" onClick={handleClearAllFilters} startIcon={<FilterAltOffIcon fontSize="small" data-id="element-2655" />} sx={{
          textTransform: 'none',
          fontSize: '0.8rem'
        }} data-id="element-2654">
              Clear Filters
            </Button>}
        </Box>
        <Typography variant="body2" color="text.secondary" data-id="element-2656">
          Showing {filteredData.length > 0 ? page * rowsPerPage + 1 : 0}-
          {Math.min((page + 1) * rowsPerPage, filteredData.length)} of{' '}
          {filteredData.length} transactions
        </Typography>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{
      borderRadius: 2,
      border: '1px solid #E5E7EB',
      boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
      overflow: 'auto',
      maxHeight: 'calc(100vh - 300px)'
    }} data-id="element-2657">
        <Table stickyHeader sx={{
        minWidth: 650
      }} aria-label="transaction table" data-id="element-2658">
          <TableHead data-id="element-2659">
            <TableRow sx={{
            backgroundColor: '#F3F5F7',
            position: 'sticky',
            top: 0,
            zIndex: 2
          }} data-id="element-2660">
              {columns.map(column => <TableCell key={column.id} sx={{
              fontWeight: 600,
              color: '#0052CC',
              borderBottom: '1px solid #E5E7EB',
              py: 2,
              backgroundColor: '#F3F5F7'
            }} data-id="element-2661">
                  {column.label}
                </TableCell>)}
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#0052CC',
              borderBottom: '1px solid #E5E7EB',
              py: 2,
              backgroundColor: '#F3F5F7'
            }} data-id="element-2662">
                Actions
              </TableCell>
            </TableRow>
            <TableRow sx={{
            backgroundColor: '#F9FAFB',
            position: 'sticky',
            top: '53px',
            zIndex: 1
          }} data-id="element-2663">
              {columns.map(column => <TableCell key={`filter-${column.id}`} sx={{
              py: 1,
              px: 1,
              borderBottom: '1px solid #E5E7EB',
              backgroundColor: '#F9FAFB'
            }} data-id="element-2664">
                  {column.filterable && <Box sx={{
                minWidth: 100
              }} data-id="element-2665">
                      {column.filterType === 'text' && <TextField size="small" placeholder="Filter..." value={filters[column.id] || ''} onChange={e => handleFilterChange(column.id, e.target.value)} fullWidth sx={{
                  '& .MuiInputBase-input': {
                    py: 0.5,
                    px: 1,
                    fontSize: '0.8rem'
                  }
                }} data-id="element-2666" />}
                      {column.filterType === 'select' && <FormControl fullWidth size="small" data-id="element-2667">
                          <Select value={filters[column.id] || 'all'} onChange={e => handleFilterChange(column.id, e.target.value)} displayEmpty sx={{
                    '& .MuiSelect-select': {
                      py: 0.5,
                      px: 1,
                      fontSize: '0.8rem'
                    }
                  }} data-id="element-2668">
                            <MenuItem value="all" data-id="element-2669">
                              <em data-id="element-2670">All</em>
                            </MenuItem>
                            {column.filterOptions?.map(option => <MenuItem key={option} value={option} data-id="element-2671">
                                {option}
                              </MenuItem>)}
                          </Select>
                        </FormControl>}
                      {column.filterType === 'date' && <Box sx={{
                  display: 'flex',
                  gap: 0.5
                }} data-id="element-2672">
                          <TextField type="date" size="small" value={filters[column.id]?.from || ''} onChange={e => handleFilterChange(column.id, {
                    ...filters[column.id],
                    from: e.target.value
                  })} sx={{
                    '& .MuiInputBase-input': {
                      py: 0.5,
                      px: 1,
                      fontSize: '0.75rem'
                    }
                  }} data-id="element-2673" />
                          <TextField type="date" size="small" value={filters[column.id]?.to || ''} onChange={e => handleFilterChange(column.id, {
                    ...filters[column.id],
                    to: e.target.value
                  })} sx={{
                    '& .MuiInputBase-input': {
                      py: 0.5,
                      px: 1,
                      fontSize: '0.75rem'
                    }
                  }} data-id="element-2674" />
                        </Box>}
                      {column.filterType === 'number' && <Box sx={{
                  display: 'flex',
                  gap: 0.5
                }} data-id="element-2675">
                          <TextField type="number" size="small" placeholder="Min" value={filters[column.id]?.min || ''} onChange={e => handleFilterChange(column.id, {
                    ...filters[column.id],
                    min: e.target.value
                  })} sx={{
                    '& .MuiInputBase-input': {
                      py: 0.5,
                      px: 1,
                      fontSize: '0.75rem'
                    }
                  }} data-id="element-2676" />
                          <TextField type="number" size="small" placeholder="Max" value={filters[column.id]?.max || ''} onChange={e => handleFilterChange(column.id, {
                    ...filters[column.id],
                    max: e.target.value
                  })} sx={{
                    '& .MuiInputBase-input': {
                      py: 0.5,
                      px: 1,
                      fontSize: '0.75rem'
                    }
                  }} data-id="element-2677" />
                        </Box>}
                    </Box>}
                </TableCell>)}
              <TableCell sx={{
              py: 1,
              px: 1,
              borderBottom: '1px solid #E5E7EB',
              backgroundColor: '#F9FAFB'
            }} data-id="element-2678" />
            </TableRow>
          </TableHead>
          <TableBody data-id="element-2679">
            {paginatedData.length > 0 ? paginatedData.map((row, index) => <TableRow key={row.id || index} sx={{
            '&:last-child td, &:last-child th': {
              border: 0
            },
            '&:hover': {
              backgroundColor: '#F9FAFB'
            },
            cursor: 'pointer',
            borderBottom: '1px solid #E5E7EB'
          }} onClick={() => onViewDetails(row)} data-id="element-2680">
                  {columns.map(column => <TableCell key={column.id} sx={{
              py: 1.5,
              color: '#374151',
              borderBottom: 'none'
            }} data-id="element-2681">
                      {column.id === 'status' ? <Chip label={row[column.id]} size="small" color={getStatusColor(row[column.id]) as any} sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                borderRadius: '20px',
                height: '24px'
              }} data-id="element-2682" /> : column.format ? column.format(row[column.id], row) : row[column.id]}
                    </TableCell>)}
                  <TableCell align="right" sx={{
              py: 1.5,
              borderBottom: 'none'
            }} data-id="element-2683">
                    <Tooltip title="View Details" data-id="element-2684">
                      <IconButton size="medium" onClick={e => {
                  e.stopPropagation();
                  onViewDetails(row);
                }} sx={{
                  color: '#6B7280',
                  '&:hover': {
                    color: '#0052CC',
                    backgroundColor: '#F0F3F7'
                  }
                }} data-id="element-2685">
                        <VisibilityIcon fontSize="small" data-id="element-2686" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>) : <TableRow data-id="element-2687">
                <TableCell colSpan={columns.length + 1} align="center" sx={{
              py: 5
            }} data-id="element-2688">
                  <Typography color="text.secondary" data-id="element-2689">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={filteredData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} data-id="element-2690" />
    </Paper>;
};
export default UnifiedTransactionTable;